import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { FaListUl, FaPlus, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { FinCheck, FinChecks, FinField, FinInput } from './FinField';
import { escolhaDeSeparadores, precisaEscolherSeparador, separarOrigens, tiposDeSeparador } from '../util/malhaAereo';
import '../css/financeiro-parcela-pagar.css';

const lista = (itens) => itens.map(i => `"${i}"`).join(', ');
const plural = (n, um, varios) => `${n} ${n === 1 ? um : varios}`;

/**
 * Caixa das origens da solicitação. O caminho principal é COLAR: a lista inteira entra de uma
 * vez — linha ou coluna do Excel, uma por linha, ou separadas por vírgula, ponto e vírgula ou
 * barra vertical (`separarOrigens`) — e a caixa volta vazia. Digitar também vale, para uma
 * origem avulsa: o texto espera o Adicionar (ou o Enter), com a mesma separação.
 *
 * Texto com mais de um tipo de separador pergunta quais separam, com a prévia do resultado:
 * numa coluna do Excel com "Salvador, BA", a vírgula é do nome.
 *
 * Repetida (sem acento e caixa, contra as que já estão na lista) e curta demais (menos de 2
 * letras) ficam de fora com aviso.
 *
 * @param {string[]} existentes origens já na lista
 * @param {Function} onAdicionar (novas: string[]) => void
 * @param {boolean}  disabled
 * @param {boolean}  obrigatorio asterisco no rótulo — a tela liga enquanto não há nenhuma origem
 * @param {Function} onFocus, onBlur foco da caixa, repassado ao input
 * @param {Function} onPerguntando (aberta: boolean) — a janela dos separadores abriu ou fechou
 * @param {Function} onTexto (temTexto: boolean) — a caixa ficou com texto digitado ou vazia
 * @param {Function} onAtualizarQuantidades ([{ chave, nome, quantidade }]) — origem já na lista
 *                   colada com outra quantidade
 *
 * `existentes` aceita os nomes ou { origem, quantidadePassageiros }: com a quantidade, colar o
 * mesmo número não conta como atualização.
 */
export default function ColarOrigens({ existentes = [], onAdicionar, disabled = false, obrigatorio = false, onFocus, onBlur, onPerguntando, onTexto, onAtualizarQuantidades }) {
    const [texto, setTexto] = useState('');
    // { texto, tipos, separar, agrupar }: o texto espera a escolha dos separadores.
    const [escolha, setEscolha] = useState(null);

    const mudarTexto = (valor) => {
        setTexto(valor);
        onTexto?.(valor.trim() !== '');
    };

    const aplicar = (bruto, separar, agrupar = []) => {
        const { novas, repetidas, curtas, quantidades, atualizacoes } = separarOrigens(bruto, existentes, separar, agrupar);
        if (atualizacoes.length) {
            onAtualizarQuantidades?.(atualizacoes);
            toast.info(`Quantidade atualizada: ${atualizacoes.map(a => `${a.nome} (${a.quantidade})`).join(', ')}.`);
        }
        if (novas.length) {
            onAdicionar(novas, quantidades);
            toast.success(novas.length === 1 ? '1 origem adicionada' : `${novas.length} origens adicionadas`);
            // A quantidade é opcional: o aviso só diz que dá para informar depois, na tabela.
            const semQuantidade = novas.filter(o => quantidades[o] === undefined).length;
            if (semQuantidade && semQuantidade !== novas.length) {
                toast.info(`${plural(semQuantidade, 'origem veio', 'origens vieram')} sem quantidade de passageiros: se souber, informe na tabela.`);
            }
        }
        if (repetidas.length) {
            toast.info(`Já estavam na lista: ${lista(repetidas)}.`);
        }
        if (curtas.length) {
            toast.warning(`Ficaram de fora, com menos de 2 letras: ${lista(curtas)}.`);
        }
        if (!novas.length && !repetidas.length && !curtas.length && !atualizacoes.length) {
            toast.info('Nenhuma origem para adicionar.');
        }
    };

    const abrirEscolha = (dados) => {
        setEscolha(dados);
        onPerguntando?.(true);
    };
    const fecharEscolha = () => {
        setEscolha(null);
        onPerguntando?.(false);
    };

    const adicionar = (bruto) => {
        const tipos = tiposDeSeparador(bruto);
        if (precisaEscolherSeparador(tipos)) {
            abrirEscolha({ texto: bruto, ...escolhaDeSeparadores(bruto) });
            return;
        }
        // Um tipo só (ou nenhum): separa por ele. O que não mudava nada já saiu da lista.
        aplicar(bruto, tipos.map(t => t.chave));
    };

    const colar = (e) => {
        const colado = e.clipboardData?.getData('text') ?? '';
        if (!colado.trim()) return;
        // O navegador trocaria as quebras de linha por espaço num input de uma linha: o texto
        // sai direto da área de transferência, e a caixa não recebe nada.
        e.preventDefault();
        adicionar(texto ? `${texto}\n${colado}` : colado);
        mudarTexto('');
    };

    const adicionarDigitado = () => {
        if (!texto.trim()) return;
        adicionar(texto);
        mudarTexto('');
    };

    // Separar e agrupar se excluem: marcar o separador numa lista o tira da outra.
    const marcar = (lista, outra, chave) => setEscolha(atual => ({
        ...atual,
        [lista]: atual[lista].includes(chave) ? atual[lista].filter(c => c !== chave) : [...atual[lista], chave],
        [outra]: atual[outra].filter(c => c !== chave),
    }));

    const confirmarEscolha = () => {
        aplicar(escolha.texto, escolha.separar, escolha.agrupar);
        fecharEscolha();
    };

    const previa = escolha ? separarOrigens(escolha.texto, existentes, escolha.separar, escolha.agrupar) : null;

    return (
        <>
            <div className="fin-pp__filters" style={{ alignItems: 'flex-end' }}>
                <FinField label="Adicionar origens e quantidades" grow required={obrigatorio}>
                    <FinInput type="text" value={texto} disabled={disabled}
                        placeholder="Cole aqui as Origens e Quantidades do Excel: separadas por linhas, colunas ou caracteres especiais ( : ; , . | · ) e escolha"
                        onPaste={colar}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        onChange={e => mudarTexto(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                adicionarDigitado();
                            }
                        }} />
                </FinField>
                <button type="button" className="fin-pp__btn fin-pp__btn--tall"
                    disabled={disabled || !texto.trim()}
                    title="Adiciona o que foi digitado (Enter também adiciona). Colar adiciona sozinho."
                    onClick={adicionarDigitado}>
                    <FaPlus size={12} />
                    Adicionar
                </button>
            </div>

            {/* No body: a caixa mora dentro de cards com overflow escondido e dentro do form
                da solicitação. */}
            {escolha && ReactDOM.createPortal(
                <div className="modal fade show d-block fin-pp fin-pp__modalbg"
                    onClick={e => { if (e.target === e.currentTarget) fecharEscolha(); }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content fin-pp__modal">
                            <div className="fin-pp__modal-head">
                                <span className="fin-pp__modal-head-ic"><FaListUl size={17} /></span>
                                <h3 className="fin-pp__modal-title">Quais separadores separam as origens?
                                    <span className="fin-pp__modal-sub">
                                        Separar divide os valores. Agrupar repete o primeiro valor do grupo na frente de cada um dos outros, como "São Paulo - Congonhas" e "São Paulo - Guarulhos". O que não estiver marcado continua no nome.
                                    </span>
                                </h3>
                                <button type="button" className="fin-pp__modal-close" aria-label="Fechar"
                                    onClick={fecharEscolha}>
                                    <FaTimes size={16} />
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="fin-pp__filters" style={{ alignItems: 'flex-start' }}>
                                    {[
                                        { lista: 'separar', outra: 'agrupar', rotulo: 'Separar por' },
                                        { lista: 'agrupar', outra: 'separar', rotulo: 'Agrupar por' },
                                    ].map(coluna => (
                                        <FinField key={coluna.lista} label={coluna.rotulo} grow>
                                            <FinChecks>
                                                {escolha.tipos.map(t => (
                                                    <FinCheck key={t.chave} id={`${coluna.lista}-origem-${t.chave}`}
                                                        label={t.rotulo}
                                                        checked={escolha[coluna.lista].includes(t.chave)}
                                                        onChange={() => marcar(coluna.lista, coluna.outra, t.chave)} />
                                                ))}
                                            </FinChecks>
                                        </FinField>
                                    ))}
                                </div>
                                <div className="fin-pp__label" style={{ marginTop: 18 }}>
                                    {previa.novas.length
                                        ? `Vão entrar ${plural(previa.novas.length, 'origem', 'origens')}`
                                        : 'Nenhuma origem nova'}
                                </div>
                                <div className="d-flex flex-wrap gap-2" style={{ marginTop: 8, maxHeight: 220, overflowY: 'auto' }}>
                                    {previa.novas.map((o, i) => (
                                        <span key={i} className="fin-pp__minitag is-info">
                                            {o}{previa.quantidades[o] !== undefined ? ` · ${plural(previa.quantidades[o], 'pax', 'pax')}` : ''}
                                        </span>
                                    ))}
                                </div>
                                {previa.atualizacoes.length > 0 && (
                                    <p className="fin-pp__cphead-sub" style={{ marginTop: 10 }}>
                                        Já estão na lista e a quantidade muda: {previa.atualizacoes.map(a => `${a.nome} (${a.quantidade})`).join(', ')}.
                                    </p>
                                )}
                                {previa.repetidas.length > 0 && (
                                    <p className="fin-pp__cphead-sub" style={{ marginTop: 10 }}>
                                        Já estão na lista: {lista(previa.repetidas)}.
                                    </p>
                                )}
                                {previa.curtas.length > 0 && (
                                    <p className="fin-pp__cphead-sub" style={{ marginTop: 6 }}>
                                        Ficam de fora, com menos de 2 letras: {lista(previa.curtas)}.
                                    </p>
                                )}
                            </div>
                            <div className="fin-pp__modal-foot">
                                <button type="button" className="fin-pp__btn" onClick={fecharEscolha}>Cancelar</button>
                                <button type="button" className="fin-pp__btn fin-pp__btn--primary"
                                    disabled={!previa.novas.length && !previa.atualizacoes.length}
                                    onClick={confirmarEscolha}>
                                    {[
                                        previa.novas.length && `Adicionar ${plural(previa.novas.length, 'origem', 'origens')}`,
                                        previa.atualizacoes.length && `${previa.novas.length ? 'atualizar' : 'Atualizar'} ${plural(previa.atualizacoes.length, 'quantidade', 'quantidades')}`,
                                    ].filter(Boolean).join(' e ') || 'Adicionar'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body,
            )}
        </>
    );
}
